import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ConfirmPhoneVerificationRequest,
  ConfirmPhoneVerificationResponse,
  ConfirmPhoneVerificationResponseSchema,
  PhoneVerificationRequest,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
  VerifyPhoneResponseSchema,
} from '../../models/phone-verification.model';
import { SmartrService } from '../smartr.service';

@Injectable({
  providedIn: 'root',
})
export class PhoneVerificationService extends SmartrService {
  private readonly http = inject(HttpClient);

  readonly phoneVerificationStatus = signal<boolean | null>(null);

  /**
   * Initiates phone verification by sending SMS with verification code
   * @param request - The request containing phone number
   * @returns Observable that emits the verification response with verification ID
   */
  initiateVerification(request: VerifyPhoneRequest): Observable<VerifyPhoneResponse> {
    return this.http
      .post<VerifyPhoneResponse>(environment.phoneVerification.initiate, request)
      .pipe(
        this.verifyResponse(VerifyPhoneResponseSchema),
        map((response: VerifyPhoneResponse) => response),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Confirms phone verification using the provided verification code
   * @param request - The request containing verification ID and code
   * @returns Observable that emits the confirmation response
   */
  confirmVerification(
    request: ConfirmPhoneVerificationRequest
  ): Observable<ConfirmPhoneVerificationResponse> {
    return this.http
      .post<ConfirmPhoneVerificationResponse>(environment.phoneVerification.confirm, request)
      .pipe(
        this.verifyResponse(ConfirmPhoneVerificationResponseSchema),
        map((response: ConfirmPhoneVerificationResponse) => response),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Checks if the phone is verified for a specific client
   * If status is already verified (true), returns cached value without API call
   * @param clientId - The client ID to check verification status for
   * @returns Observable that emits the verification status
   */
  checkPhoneVerificationStatus(clientId: number): Observable<{ verified: boolean }> {
    const currentStatus = this.phoneVerificationStatus();
    if (currentStatus === true) {
      return of({ verified: true });
    }

    const url = environment.phoneVerification.status.replace('{{clientId}}', clientId.toString());

    return this.http.get<boolean>(url).pipe(
      map((raw) => {
        const verified = !!raw;
        this.phoneVerificationStatus.set(verified);
        return { verified };
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Sets the phone verification status to true for a specific client
   * Called when verification is successful
   * @param clientId - The client ID to update verification status for
   * @param phoneNumber - The phone number that was verified
   * @returns Observable that emits the success status
   */
  setPhoneVerificationStatus(
    clientId: number,
    phoneNumber: string
  ): Observable<{ success: boolean; message: string }> {
    const url = environment.phoneVerification.update.replace('{{clientId}}', clientId.toString());
    const request: PhoneVerificationRequest = {
      phoneNumber: phoneNumber,
    };

    return this.http.put<{ success: boolean; message: string }>(url, request).pipe(
      map((response) => {
        if (response.success) {
          this.phoneVerificationStatus.set(true);
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Handles HTTP errors and maps them to appropriate error messages
   * @param error - The HTTP error response
   * @returns Observable that throws an error with appropriate message
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';

    switch (error.status) {
      case 0:
        errorMessage = 'Network error. Please check your connection and try again.';
        break;
      case 400:
        errorMessage = 'Invalid request. Please check your phone number and try again.';
        break;
      case 404:
        errorMessage = 'Phone verification service not found.';
        break;
      case 429:
        errorMessage = 'Too many attempts. Please wait before trying again.';
        break;
      case 500:
        errorMessage = 'Server error. Please try again later.';
        break;
    }

    return throwError(() => new Error(errorMessage));
  }
}
